/*
 * Copyright 2019 Teralytics
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import { rollup } from 'd3-array';
import KDBush from 'kdbush';
var defaultOptions = {
    minZoom: 0,
    maxZoom: 16,
    radius: 40,
    extent: 512,
    nodeSize: 64,
    makeClusterName: function (id, numPoints) { return undefined; },
    makeClusterId: function (id) { return "{[" + id + "]}"; },
};
export function isLeafPoint(p) {
    var index = p.index;
    return index != null;
}
export function isClusterPoint(p) {
    var id = p.id;
    return id != null;
}
export function clusterLocations(locations, locationAccessors, getLocationWeight, options) {
    var e_1, _a;
    var getLocationCentroid = locationAccessors.getLocationCentroid, getLocationId = locationAccessors.getLocationId;
    var opts = __assign(__assign({}, defaultOptions), options);
    var minZoom = opts.minZoom, maxZoom = opts.maxZoom, nodeSize = opts.nodeSize, makeClusterName = opts.makeClusterName, makeClusterId = opts.makeClusterId;
    var trees = new Array(maxZoom + 1);
    // generate a cluster object for each point and index input points into a KD-tree
    var clusters = new Array();
    for (var i = 0; i < locations.length; i++) {
        var _b = __read(getLocationCentroid(locations[i]), 2), x = _b[0], y = _b[1];
        clusters.push({
            x: lngX(x),
            y: latY(y),
            weight: getLocationWeight(getLocationId(locations[i])),
            zoom: Infinity,
            index: i,
            parentId: -1,
        });
    }
    trees[maxZoom + 1] = new KDBush(clusters, getX, getY, nodeSize, Float32Array);
    // cluster points on max zoom, then cluster the results on previous zoom, etc.;
    // results in a cluster hierarchy across zoom levels
    for (var z = maxZoom; z >= minZoom; z--) {
        // create a new set of clusters for the zoom and index them with a KD-tree
        clusters = cluster(clusters, z, trees[z + 1], opts);
        trees[z] = new KDBush(clusters, getX, getY, nodeSize, Float32Array);
    }
    if (trees.length === 0) {
        return [];
    }
    var numbersOfClusters = trees.map(function (d) { return d.points.length; });
    var maxAvailZoom = numbersOfClusters.indexOf(numbersOfClusters[numbersOfClusters.length - 1]);
    var minAvailZoom = Math.min(maxAvailZoom, numbersOfClusters.lastIndexOf(numbersOfClusters[0]));
    var clusterLevels = new Array();
    for (var zoom = minAvailZoom; zoom <= maxAvailZoom; zoom++) {
        var childrenByParent = void 0;
        var tree = trees[zoom];
        if (zoom < maxAvailZoom) {
            childrenByParent = rollup(trees[zoom + 1].points, function (points) { return points.map(function (p) { return (p.id ? makeClusterId(p.id) : getLocationId(locations[p.index])); }); }, function (point) { return point.parentId; });
        }
        var nodes = [];
        try {
            for (var _c = (e_1 = void 0, __values(tree.points)), _d = _c.next(); !_d.done; _d = _c.next()) {
                var point = _d.value;
                var x = point.x, y = point.y, numPoints = point.numPoints;
                if (isLeafPoint(point)) {
                    var location_1 = locations[point.index];
                    nodes.push({
                        id: getLocationId(location_1),
                        zoom: zoom,
                        centroid: getLocationCentroid(location_1),
                    });
                }
                else if (isClusterPoint(point)) {
                    var id = point.id;
                    var children = childrenByParent && childrenByParent.get(id);
                    if (!children) {
                        throw new Error("Cluster " + id + " doesn't have children");
                    }
                    nodes.push({
                        id: makeClusterId(id),
                        name: makeClusterName(id, numPoints),
                        zoom: zoom,
                        centroid: [xLng(x), yLat(y)],
                        children: children,
                    });
                }
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (_d && !_d.done && (_a = _c.return)) _a.call(_c);
            }
            finally { if (e_1) throw e_1.error; }
        }
        clusterLevels.push({
            zoom: zoom,
            nodes: nodes,
        });
    }
    return clusterLevels;
}
function createCluster(x, y, id, numPoints, weight) {
    return {
        x: x,
        y: y,
        zoom: Infinity,
        id: id,
        parentId: -1,
        numPoints: numPoints,
        weight: weight,
    };
}
function cluster(points, zoom, tree, options) {
    var e_2, _a;
    var clusters = [];
    var radius = options.radius, extent = options.extent;
    var r = radius / (extent * Math.pow(2, zoom));
    // loop through each point
    for (var i = 0; i < points.length; i++) {
        var p = points[i];
        // if we've already visited the point at this zoom level, skip it
        if (p.zoom <= zoom) {
            continue;
        }
        p.zoom = zoom;
        // find all nearby points
        var neighborIds = tree.within(p.x, p.y, r);
        var weight = p.weight || 1;
        var numPoints = isClusterPoint(p) ? p.numPoints : 1;
        var wx = p.x * weight;
        var wy = p.y * weight;
        // encode both zoom and point index on which the cluster originated
        var id = (i << 5) + (zoom + 1);
        try {
            for (var neighborIds_1 = (e_2 = void 0, __values(neighborIds)), neighborIds_1_1 = neighborIds_1.next(); !neighborIds_1_1.done; neighborIds_1_1 = neighborIds_1.next()) {
                var neighborId = neighborIds_1_1.value;
                var b = tree.points[neighborId];
                // filter out neighbors that are already processed
                if (b.zoom <= zoom) {
                    continue;
                }
                b.zoom = zoom; // save the zoom (so it doesn't get processed twice)
                var weight2 = b.weight || 1;
                var numPoints2 = b.numPoints || 1;
                wx += b.x * weight2; // accumulate coordinates for calculating weighted center
                wy += b.y * weight2;
                weight += weight2;
                numPoints += numPoints2;
                b.parentId = id;
            }
        }
        catch (e_2_1) { e_2 = { error: e_2_1 }; }
        finally {
            try {
                if (neighborIds_1_1 && !neighborIds_1_1.done && (_a = neighborIds_1.return)) _a.call(neighborIds_1);
            }
            finally { if (e_2) throw e_2.error; }
        }
        if (numPoints === 1) {
            clusters.push(p);
        }
        else {
            p.parentId = id;
            clusters.push(createCluster(wx / weight, wy / weight, id, numPoints, weight));
        }
    }
    return clusters;
}
// spherical mercator to longitude/latitude
function xLng(x) {
    return (x - 0.5) * 360;
}
function yLat(y) {
    var y2 = ((180 - y * 360) * Math.PI) / 180;
    return (360 * Math.atan(Math.exp(y2))) / Math.PI - 90;
}
// longitude/latitude to spherical mercator in [0..1] range
function lngX(lng) {
    return lng / 360 + 0.5;
}
function latY(lat) {
    var sin = Math.sin((lat * Math.PI) / 180);
    var y = 0.5 - (0.25 * Math.log((1 + sin) / (1 - sin))) / Math.PI;
    return y < 0 ? 0 : y > 1 ? 1 : y;
}
function getX(p) {
    return p.x;
}
function getY(p) {
    return p.y;
}
//# sourceMappingURL=cluster.js.map